import { useCallback, useEffect, useRef } from 'react';
import utils from '@happylife-a/utils';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useUser } from '../../../contexts/UserContext';
import FavoriteServiceV2 from '../../services/v2/FavoriteServiceV2';

const favoriteServiceV2 = FavoriteServiceV2();

export const REACT_QUERY_GET_FAVORITES_V2_IDS_QUERY_KEY = 'getFavoritesV2Ids';
export const REACT_QUERY_GET_FAVORITES_V2_QUERY_KEY = 'getFavoritesV2';
export const REACT_QUERY_GET_INFINITE_FAVORITES_V2_QUERY_KEY = 'getInfiniteFavoritesV2';

const QUERY_KEY_FAVORITES_IDS = [REACT_QUERY_GET_FAVORITES_V2_IDS_QUERY_KEY];
const QUERY_KEY_INFINITE_FAVORITES = [REACT_QUERY_GET_INFINITE_FAVORITES_V2_QUERY_KEY];

export const useGetFavoritesV2Ids = (options = {}) => {
  const query = useQuery({
    queryKey: QUERY_KEY_FAVORITES_IDS,
    queryFn: favoriteServiceV2.getFavoritesIds,
    ...options,
  });

  return {
    ...query,
    data: query.data || {},
  };
};

export const useGetFavoritesV2 = (options = {}) => {
  const query = useQuery({
    queryKey: [REACT_QUERY_GET_FAVORITES_V2_QUERY_KEY],
    queryFn: () => favoriteServiceV2.getFavorites(),
    ...options,
  });

  return {
    ...query,
    data: query.data || {},
  };
};

export const useGetInfiniteFavoritesV2 = (variables, options = {}) => {
  const query = useInfiniteQuery({
    queryKey: [REACT_QUERY_GET_INFINITE_FAVORITES_V2_QUERY_KEY, variables],
    queryFn: ({ pageParam = 0 }) => {
      if (pageParam === null) {
        return { favorites: [], meta: {} };
      }

      return favoriteServiceV2.getFavorites({
        ...variables,
        offset: pageParam,
      });
    },
    getNextPageParam: (lastPage) => {
      const { offset, limit, total } = lastPage.meta || {};
      const nextOffset = offset + limit;
      return nextOffset < total ? nextOffset : undefined;
    },
    ...options,
  });

  return query;
};

export const useCreateFavoriteV2 = () => {
  return useMutation({ mutationFn: favoriteServiceV2.createFavorite });
};

export const useDeleteFavoriteV2 = () => {
  return useMutation({ mutationFn: favoriteServiceV2.deleteFavorite });
};

export const useToggleFavoriteV2 = (isFavorite) => {
  const { user, setOptimisticUpdate } = useUser();
  const queryClient = useQueryClient();
  const { mutate: deleteFavorite } = useDeleteFavoriteV2();
  const { mutate: createFavorite } = useCreateFavoriteV2();

  // Use ref to always read fresh cache — avoids stale closure on rapid clicks
  const refFavorites = useRef();
  refFavorites.current = queryClient.getQueryData(QUERY_KEY_FAVORITES_IDS);

  const toggleFavorite = useCallback(
    (productVariantId, { onCreate, onDelete } = {}) => {
      if (isFavorite(productVariantId)) {
        // Find the favorite record ID for this variant
        const favoriteRecord = refFavorites.current?.ids?.find((fav) =>
          utils.helpers.id.same(fav.productVariantId, productVariantId)
        );

        if (!favoriteRecord?.id) {
          return;
        }

        const onDeleteSuccess = () => {
          queryClient.setQueriesData({ queryKey: QUERY_KEY_FAVORITES_IDS }, (data) => {
            if (!data) return data;
            return {
              ...data,
              ids: data.ids.filter(
                (fav) => !utils.helpers.id.same(fav.productVariantId, productVariantId)
              ),
            };
          });

          queryClient.setQueriesData({ queryKey: QUERY_KEY_INFINITE_FAVORITES }, (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                favorites: page.favorites.filter(
                  (item) => !utils.helpers.id.same(item.productVariantId, productVariantId)
                ),
              })),
            };
          });

          if (typeof onDelete === 'function') {
            onDelete(null);
          }
        };

        deleteFavorite({ favoriteId: favoriteRecord.id }, { onSuccess: onDeleteSuccess });
      } else {
        const onCreateSuccess = (response) => {
          const newFavorite = {
            id: response.id,
            productId: response.productId,
            productVariantId: response.productVariantId,
          };

          queryClient.setQueryData(QUERY_KEY_FAVORITES_IDS, (data) => {
            data = data || { ids: [] };
            return {
              ...data,
              ids: [...data.ids, newFavorite],
            };
          });

          if (typeof onCreate === 'function') {
            onCreate(newFavorite);
          }
        };

        createFavorite({ productVariantId }, { onSuccess: onCreateSuccess });
      }
    },
    [isFavorite, deleteFavorite, createFavorite, queryClient]
  );

  useEffect(() => {
    const favorites = refFavorites.current;
    if (!favorites) {
      return;
    }

    const favoritesCount = favorites.ids?.length || 0;
    setOptimisticUpdate({
      ...user,
      customerInfo: { ...user.customerInfo, favoritesCount: favoritesCount },
    });
  }, [refFavorites.current]);

  return toggleFavorite;
};
